/**
 * Feature Discovery Tracking Hook
 *
 * Tracks when users discover features for the first time.
 * Used to measure feature discoverability and adoption.
 */

import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { canTrack } from "./session";
import type { DiscoverableFeature, DiscoveryPath } from "@dmarc-platform/shared";

// Track which features have been discovered this session to avoid duplicate calls
const discoveredThisSession = new Set<string>();

/**
 * Hook to track feature discovery
 *
 * Usage:
 * ```tsx
 * function SourcesPage() {
 *   useFeatureDiscovery("sources");
 *   return <div>...</div>;
 * }
 * ```
 *
 * @param featureKey - The feature being discovered
 * @param discoveryPath - How the user found the feature (optional)
 */
export function useFeatureDiscovery(
  featureKey: DiscoverableFeature,
  discoveryPath: DiscoveryPath = "navigation"
): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (!canTrack()) return;

    // Only track once per session per feature
    if (tracked.current || discoveredThisSession.has(featureKey)) {
      return;
    }

    tracked.current = true;
    discoveredThisSession.add(featureKey);

    // Get the referrer path if available
    let sourcePage: string | undefined;
    try {
      if (document.referrer) {
        const referrerUrl = new URL(document.referrer);
        // Only use referrer if it's from the same origin
        if (referrerUrl.origin === window.location.origin) {
          sourcePage = referrerUrl.pathname;
        }
      }
    } catch {
      // Ignore invalid referrer URLs
    }

    api
      .trackFeatureDiscovery({
        featureKey,
        discoveryPath,
        sourcePage,
      })
      .catch(() => {
        // Silently fail - analytics should never break the app
      });
  }, [featureKey, discoveryPath]);
}

/**
 * Imperatively track feature discovery
 * Use this for features discovered through user actions (not page loads)
 *
 * @param featureKey - The feature being discovered
 * @param discoveryPath - How the user found the feature
 * @param sourcePage - The page they were on when they discovered it
 */
export function trackFeatureDiscovery(
  featureKey: DiscoverableFeature,
  discoveryPath: DiscoveryPath = "navigation",
  sourcePage?: string
): void {
  if (!canTrack()) return;

  // Only track once per session per feature
  if (discoveredThisSession.has(featureKey)) {
    return;
  }

  discoveredThisSession.add(featureKey);

  api
    .trackFeatureDiscovery({
      featureKey,
      discoveryPath,
      sourcePage: sourcePage || window.location.pathname,
    })
    .catch(() => {
      // Silently fail
    });
}
